// 扑克牌动画：1、发牌动画；2、开奖动画
import { ref, nextTick } from 'vue';
import gsapDefault from 'gsap';
// 导入牌型工具函数（已整合到 gameTps.ts）
import { calculateHandType, getAllCombinations } from './gameTps';

// 动画停止标志 - 用于退出房间时停止所有动画回调
let animationStopped = false;

// 🔧 收牌动画防重复标志
let isCollectingCards = false;

/**
 * 卡牌花色（四种标准花色
 */
export type CardSuit =
  | 'hearts'    // 红桃 
  | 'diamonds'  // 方块
  | 'clubs'     // 梅花
  | 'spades'    // 黑桃
/**
 * 卡牌等级（13种标准牌型
 */
export type CardRank =
  | 'A' | '2' | '3' | '4' | '5'
  | '6' | '7' | '8' | '9' | '10'
  | 'J' | 'Q' | 'K'

/**
 * 游戏区域标识（天地玄黄+庄家）
 */
export type AreaId =
  | 'tian'    // 天
  | 'di'     // 地
  | 'xuan'   // 玄
  | 'huang'    // 黄
  | 'banker'    // 庄家区

// 卡牌接口定义
export interface Card {
  suit: CardSuit;
  rank: CardRank;
  value: number;
}

// 反应式状态管理
// 记录卡牌的引用
// 定义PokerCard组件接口
interface PokerCardComponent {
  $el: HTMLElement;
  flipCard?: () => void;
  toggleFaceUp?: () => void;
  toggleFaceDown?: () => void;
  setCardName?: (cardName: string) => void;
  isFaceUp?: boolean;
}

// Vue组件或DOM元素类型
type CardElement = HTMLElement | PokerCardComponent | any;

export const cardRefs = ref(new Map<string, CardElement>());

/**
 * 获取元素的DOM节点，无论是组件还是原生元素
 * @param element Vue组件或DOM元素
 * @returns DOM元素
 */
const getDOMElement = (element: CardElement): HTMLElement => {
  if (!element) return null;

  // 如果是原生元素或至少有getBoundingClientRect方法
  if ('getBoundingClientRect' in element && typeof element.getBoundingClientRect === 'function') {
    return element as HTMLElement;
  }

  // 如果是Vue组件
  if (element.$el && 'getBoundingClientRect' in element.$el) {
    return element.$el as HTMLElement;
  }

  console.warn('无法获取元素的DOM节点', element);
  return null;
};

// 各区域卡牌
export const areaCards = ref<Record<AreaId, Card[]>>({} as Record<AreaId, Card[]>);

// 完整牌组
export const fullDeck = ref<Card[]>([]);

/**
 * 清理所有卡牌引用
 * 在每局游戏结束后清空所有卡牌状态，确保新一局发牌动画与初始局相同
 */
/**
 * 清理卡牌引用，准备新一局发牌
 */
export const clearCardRefs = () => {

  // 清空卡牌引用
  cardRefs.value.clear();

  // 重置区域卡牌
  areaCards.value = {
    'tian': [],
    'di': [],
    'xuan': [],
    'huang': [],
    'banker': []
  };

  // 重置牌组
  fullDeck.value = [];
};

/**
 * 彻底清理卡牌元素，仅清理状态和引用，避免直接操作DOM
 */
export const cleanupCards = () => {

  try {
    // 仅清理卡牌引用和状态，避免直接操作DOM
    clearCardRefs();

    // 使用GSAP隐藏卡牌而不直接移除DOM元素
    try {
      const gsap = window.gsap;
      if (gsap) {
        // 使用GSAP设置不可见
        gsap.set('.pocker .poker-card', { opacity: 0, display: 'none' });
      }
    } catch (e) {
      console.warn('使用GSAP隐藏卡牌失败:', e);
    }

    // 清除事件监听器
    try {
      // 尝试清除可能的事件监听器，避免内存泄漏
      cardRefs.value.forEach((card) => {
        if (card && typeof card.$destroy === 'function') {
          // 如果是Vue组件，尝试销毁它
          try {
            card.$destroy();
          } catch (innerE) {
            // 忽略销毁错误
          }
        }
      });
    } catch (e) {
      // 忽略清除事件错误
    }

  } catch (error) {
    console.error('清理卡牌元素时出错:', error);
  }
};

// GSAP类型声明
declare global {
  interface Window {
    gsap: any;
  }
}

// GSAP 模块缓存
let gsap: any = null;

/**
 * 加载GSAP模块 - 带缓存和错误处理的异步函数
 */
export const loadGSAP = async () => {
  // 如果已经加载过GSAP，直接返回缓存的实例
  if (gsap) {
    return gsap;
  }

  try {
    gsap = gsapDefault;
    return gsap;
  } catch (error) {
    console.error('GSAP 模块加载失败:', error instanceof Error ? error.message : '未知错误');
    throw new Error('无法加载动画库，请检查依赖安装或刷新页面');
  }
}

/**
 * 设置卡牌引用
 */
export const setAreaCardRef = (areaIndex: number, cardIndex: number, el: CardElement | null) => {
  if (el) {
    // 创建唯一的键，例如 'area0-card1'
    const key = `area${areaIndex}-card${cardIndex}`;
    cardRefs.value.set(key, el);

    // 如果是PokerCard组件，确保初始化为背面朝上
    const pokerCard = el as PokerCardComponent;
    if (pokerCard.$el && typeof pokerCard.toggleFaceDown === 'function') {
      pokerCard.toggleFaceDown();
    }
  }
}

/**
 * 获取特定区域的卡牌
 */
export const getAreaCards = (index: number): Card[] => {
  const areaId = getAreaId(index);
  return areaCards.value[areaId] || [];
};

/**
 * 获取区域ID
 * @param index 0-4，分别表示天、地、现、黄、庄家
 */
export const getAreaId = (index: number): AreaId => {
  switch (index) {
    case 0: return 'tian';
    case 1: return 'di';
    case 2: return 'xuan';
    case 3: return 'huang';
    case 4: return 'banker';
    default: throw new Error(`无效的区域索引: ${index}`);
  }
};
/**
 * 使用指定卡牌数据发牌
 * @param bankerCardIds 庄家卡牌ID数组
 * @param sectorCardIds 区域卡牌ID对象
 * @param executeAnimation 是否执行发牌动画
 * @returns 更新后的卡牌数据
 */
/**
 * 强制所有卡牌显示背面状态（性能优化版）
 */
export const ensureAllCardsShowBack = () => {
  // 使用for...of代替forEach，性能更好
  for (const [key, cardEl] of cardRefs.value.entries()) {
    if (!cardEl) continue;

    // 调用组件的toggleFaceDown方法
    if (typeof cardEl.toggleFaceDown === 'function') {
      cardEl.toggleFaceDown();
    }

    // 强制设置DOM元素为背面状态
    const domEl = getDOMElement(cardEl);
    if (domEl) {
      // 只清除必要的属性，避免clearProps: "all"的性能开销
      if (window.gsap) {
        window.gsap.set(domEl, {
          clearProps: "x,y,scale,rotation,rotationY,transformOrigin,zIndex"
        });
      }

      // 重置整张卡牌的transform，保持CSS定位
      domEl.style.transform = 'translateX(-50%) translateZ(0)';

      // 🔧 重置z-index，根据卡牌索引设置层级（0-4 → 1-5）
      const cardIndex = parseInt(key.split('-')[1]) || 0;
      domEl.style.zIndex = String(cardIndex + 1);

      // 缓存子元素查询结果
      const frontEl = domEl.querySelector('.front') as HTMLElement;
      const backEl = domEl.querySelector('.back') as HTMLElement;
      if (frontEl) {
        // 使用单独属性设置，避免覆盖Vue绑定的雪碧图样式
        frontEl.style.transform = 'rotateY(180deg)';
        frontEl.style.transformOrigin = 'center center';
      }
      if (backEl) {
        backEl.style.transform = 'rotateY(0deg)';
        backEl.style.transformOrigin = 'center center';
      }
    }
  }
};

/**
 * 使用指定卡牌数据发牌
 * @param bankerCardIds 庄家卡牌ID数组
 * @param animationDuration 动画时长
 * @param skipBackFlip 跳过背面翻转（用于页面恢复时直接显示正面）
 * @returns 更新后的卡牌数据
 */
export const dealCardsWithData = async (
  bankerCards: Card[],
  sectorCards: Record<string, Card[]>,
  executeAnimation: boolean = true,
  animationDuration: number = 0,
  skipBackFlip: boolean = false
) => {
  try {
    // 🛡️ 防冲突：始终停止所有卡牌动画（即使 isCollectingCards 为 false，GSAP timeline 可能仍在运行）
    if (window.gsap) {
      const cardElements = document.querySelectorAll('.pocker .card');
      if (cardElements.length > 0) {
        window.gsap.killTweensOf(cardElements);
        console.log('[发牌] 已停止所有卡牌动画');
      }
    }
    if (isCollectingCards) {
      console.log('[发牌] 检测到收牌标志，重置');
      isCollectingCards = false;
    }

    // 🔧 优化：发牌前先重置所有卡牌状态（清除收牌动画留下的 opacity: 0 等属性）
    const existingCards = document.querySelectorAll('.pocker .card') as NodeListOf<HTMLElement>;
    if (existingCards.length > 0) {
      existingCards.forEach(card => {
        // 🔧 关键：清除上一局的 niu-lifted 标记
        delete card.dataset.niuLifted;
      });
      if (window.gsap) {
        window.gsap.set(existingCards, { clearProps: 'all' });
        // 🔧 关键：确保卡牌可见（覆盖收牌动画的 opacity: 0）
        window.gsap.set(existingCards, { opacity: 1 });
      }
      // 🔧 iOS兼容：clearProps后恢复CSS居中transform，防止卡牌向左偏移
      existingCards.forEach(card => {
        card.style.transform = 'translateX(-50%) translateZ(0)';
      });
      console.log(`[发牌] 重置 ${existingCards.length} 张卡牌状态`);
    }

    // 🎯 防止发牌动画触发滚动条：临时隐藏容器溢出
    const gameContainer = document.querySelector('.game-container') as HTMLElement;
    let originalOverflow = '';


    if (executeAnimation && gameContainer) {
      // 保存原始 overflow 设置
      originalOverflow = gameContainer.style.overflow || '';
      // 临时隐藏溢出（防止卡牌飞入时触发滚动条）
      gameContainer.style.overflow = 'hidden';
    }

    // 直接使用已处理的卡牌数据，不再重复处理
    // 更新响应式状态
    const newAreaCards: Record<AreaId, Card[]> = {
      'tian': sectorCards.tian || [],
      'di': sectorCards.di || [],
      'xuan': sectorCards.xuan || [],
      'huang': sectorCards.huang || [],
      'banker': bankerCards
    };

    areaCards.value = newAreaCards;

    // 如果需要执行动画
    if (executeAnimation) {

      // 🔧 优化：等待 Vue 更新 DOM 后再开始动画，避免卡顿
      await nextTick();

      // 预处理：隐藏所有卡牌准备动画（批量处理优化）
      const allCards = document.querySelectorAll('.pocker .card');
      if (allCards.length > 0 && gsap) {
        gsap.set(allCards, {
          opacity: 0,
          scale: 0.9,
          y: 10
        });
      }

      // 🔧 优化：减少延迟时间，使用 requestAnimationFrame 确保下一帧开始动画
      await new Promise(resolve => requestAnimationFrame(resolve));

      try {
        await animateCards(animationDuration);

        // 发牌动画完成后的后处理
        setTimeout(() => {
          if (!skipBackFlip) {
            ensureAllCardsShowBack();
          }

          // 🔧 确保所有卡牌都可见并清理 GSAP 的 x/y 属性
          const allCards = document.querySelectorAll('.pocker .card') as NodeListOf<HTMLElement>;
          allCards.forEach((card) => {
            card.style.opacity = '1';
            // 清理 GSAP 设置的 transform（包含 x/y），恢复 CSS 原始 transform
            if (window.gsap) {
              window.gsap.set(card, { clearProps: 'x,y,scale' });
            }
            // 🔧 关键：clearProps 后必须恢复 CSS 居中 transform
            card.style.transform = 'translateX(-50%) translateZ(0)';
          });

          // ✅ 发牌动画完全结束后，恢复容器溢出设置
          if (gameContainer) {
            gameContainer.style.overflow = originalOverflow || '';
          }
        }, 100);
      } catch (err) {
        console.error('❌ [发牌动画] 动画执行失败:', err);
        if (!skipBackFlip) {
          ensureAllCardsShowBack();
        }
        if (gameContainer) {
          gameContainer.style.overflow = originalOverflow || '';
        }
      }
    } else {
      // 不执行动画时，立即显示卡牌（无延迟，避免闪烁）
      await nextTick();

      // 立即设置所有卡牌可见
      const allCards = document.querySelectorAll('.pocker .card');
      if (allCards.length > 0) {
        allCards.forEach((card: HTMLElement) => {
          if (card.style) {
            card.style.opacity = '1';
            // 🔧 修复：显式设置 CSS 居中 transform（某些浏览器清空后不恢复 CSS 样式）
            card.style.transform = 'translateX(-50%) translateZ(0)';
            card.style.visibility = 'visible';
          }
          const frontEl = card.querySelector('.front') as HTMLElement;
          const backEl = card.querySelector('.back') as HTMLElement;
          if (skipBackFlip) {
            // 🔧 页面恢复时：直接显示正面（front 朝前，back 朝后）
            if (frontEl) frontEl.style.transform = 'rotateY(0deg)';
            if (backEl) backEl.style.transform = 'rotateY(180deg)';
          } else {
            // 正常发牌：显示背面（front 朝后，back 朝前）
            if (frontEl) frontEl.style.transform = 'rotateY(180deg)';
            if (backEl) backEl.style.transform = 'rotateY(0deg)';
          }
        });
      }
      // 🔧 如果 skipBackFlip 为 true，跳过 ensureAllCardsShowBack
      if (!skipBackFlip) {
        ensureAllCardsShowBack();
      }
    }

    return {
      bankerCards,
      sectorCards,
      areaCards: newAreaCards
    };
  } catch (error) {
    console.error('使用指定数据发牌时出错:', error);
    throw new Error('指定数据发牌失败');
  }
};

/**
 * 卡牌动画 - 创建扑克牌发牌的动画效果
 * @param totalDuration 发牌总时长（秒），默认1.5秒
 */
export const animateCards = async (totalDuration: number = 1.5): Promise<void> => {
  try {
    // 加载GSAP动画库
    const gsap = await loadGSAP();
    if (!gsap) throw new Error('GSAP未加载');

    // 创建动画时间轴
    const timeline = gsap.timeline({
      defaults: {
        duration: 0,
        ease: 'power2.out', // 更流畅的缓动曲线
        overwrite: 'auto'
      }
    });

    // 发牌起始点 - 庄家区域中心（页面内，避免撤开页面导致滚动）
    const bankerArea = document.querySelector('.indoor-header .hand-type-result') as HTMLElement;
    const dealerPosition = bankerArea ? {
      x: bankerArea.getBoundingClientRect().left + bankerArea.offsetWidth / 2,
      y: bankerArea.getBoundingClientRect().top + bankerArea.offsetHeight / 2
    } : {
      x: window.innerWidth / 2,
      y: 100 // 默认顶部中心，不会撤开页面
    };

    // 顺时针发牌顺序
    const dealingOrder = [0, 1, 2, 3, 4]; // 天、地、玄、黄、庄家

    // 初始延迟
    let currentDelay = 0;
    // 发牌间隔优化，更流畅的发牌节奏
    const singleCardDelay = 0.025; // 每张牌间隔0.025秒

    // 一张一张发牌，每轮先给全部区域发同一位置的牌
    for (let round = 0; round < 5; round++) { // 5轮发牌，每轮给5个区域各发一张
      // 每轮开始时重置延迟，让同一轮的牌几乎同时发出
      const roundStartDelay = currentDelay;

      for (const areaIndex of dealingOrder) { // 顺时针顺序发牌
        const cardIndex = round; // 当前轮数对应卡牌索引
        const key = `area${areaIndex}-card${cardIndex}`;
        const cardEl = cardRefs.value.get(key);

        if (!cardEl) continue;

        // 获取DOM元素
        const domElement = getDOMElement(cardEl);
        if (!domElement) continue;

        // 确保卡牌显示背面状态
        if (cardEl && typeof cardEl.toggleFaceDown === 'function') {
          cardEl.toggleFaceDown();
        }

        // 强制设置卡牌为背面状态（GSAP控制）- 缓存查询结果
        const frontEl = domElement.querySelector('.front') as HTMLElement;
        const backEl = domElement.querySelector('.back') as HTMLElement;
        if (frontEl) {
          // 使用单独属性设置，避免覆盖Vue绑定的雪碧图样式
          frontEl.style.transform = 'rotateY(180deg)';
        }
        if (backEl) {
          backEl.style.transform = 'rotateY(0deg)';
        }

        // 判断使用哪种发牌模式
        const isFastMode = totalDuration <= 0.2; // 快速发牌模式（0.15秒及以下）

        // 🔧 修复：使用 x/y 偏移动画，不改变 position/left/top
        // 卡牌已经在 CSS 正确位置，只需用 x/y 偏移到庄家位置再动画回来

        // 获取卡牌当前的视觉位置（已包含 CSS left + translateX(-50%)）
        const cardRect = domElement.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;

        // 计算从当前位置到庄家位置的偏移量
        const offsetX = dealerPosition.x - cardCenterX;
        const offsetY = dealerPosition.y - cardCenterY;

        const duration = isFastMode ? 0.15 : Math.max(0.3, totalDuration * 0.3);

        // 设置初始状态：用 x/y 偏移到庄家位置
        // 🔧 添加 GPU 加速
        domElement.style.willChange = 'transform, opacity';
        domElement.style.backfaceVisibility = 'hidden';

        gsap.set(domElement, {
          x: offsetX,
          y: offsetY,
          opacity: 0,
          scale: isFastMode ? 0.85 : 0.92,
          rotationY: 0,
          zIndex: 100,
          force3D: true // 强制 GPU 加速
        });

        // 飞行动画 - x/y 回到 0（即回到 CSS 原始位置）
        timeline.to(
          domElement,
          {
            x: 0,
            y: 0,
            opacity: 1,
            scale: 1,
            duration,
            ease: 'power1.out', // 更轻快的缓动，减少卡顿感
            force3D: true,
            onComplete() {
              // 🔧 关键修复：清除 GSAP 的 transform 并恢复 CSS 居中
              gsap.set(domElement, { clearProps: 'x,y,scale,force3D' });
              domElement.style.transform = 'translateX(-50%) translateZ(0)';
              domElement.style.zIndex = String(cardIndex + 1);
              domElement.style.willChange = 'auto'; // 动画结束后释放
            }
          },
          currentDelay
        );

        // 动态调整卡牌间隔（更均匀的间隔减少卡顿）
        const cardInterval = isFastMode ? 0.02 : 0.015;
        currentDelay = roundStartDelay + (areaIndex * cardInterval);
      }

      // 动态调整轮次间隔（稍微加大间隔让动画更流畅）
      const roundInterval = totalDuration <= 0.2 ? 0.1 : 0.12;
      currentDelay = roundStartDelay + roundInterval;
    }

    // 动画结束后的全局回调
    return new Promise<void>((resolve) => {
      timeline.eventCallback('onComplete', () => {
        resolve();
      });
    });
  } catch (error) {
    console.error('执行卡牌动画时出错:', error instanceof Error ? error.message : '未知错误');
    throw new Error('动画执行失败');
  }
};

/**
 * 的三张找出组成10倍数牌
 * 根据牛牛规则，从5张牌中找出3张点数之和为10的倍数的牌
 * @param cards 5张牌的数组
 * @returns 组成10倍数的3张牌的索引数组，如果没有找到则返回空数组
 */
export const findTenMultipleCards = (cards: Card[]): number[] => {
  if (!cards || cards.length !== 5) return [];

  // 使用getAllCombinations函数获取所有可能的3张牌组合
  const combinations = getAllCombinations(cards, 3);

  // 遍历所有组合，找到第一个和为10倍数的组合
  for (const combo of combinations) {
    const sum = combo.reduce((acc, card) => acc + card.value, 0);
    if (sum % 10 === 0) {
      // 返回这3张牌在原始数组中的索引
      return combo.map(comboCard => cards.findIndex(card =>
        card.suit === comboCard.suit && card.rank === comboCard.rank
      ));
    }
  }

  return [];
};

/**
 * 重新排序卡牌数组，将10倍数的牌放到前三个位置
 * @param cards 原始卡牌数组
 * @param tenMultipleIndices 组成10倍数的三张牌的索引数组
 * @returns 重新排序后的卡牌数组
 */
export const reorderCards = (cards: Card[], tenMultipleIndices: number[]): Card[] => {
  if (!cards || cards.length !== 5 || !tenMultipleIndices || tenMultipleIndices.length !== 3) {
    return [...cards]; // 返回原始卡牌的浅拷贝
  }

  const reorderedCards: Card[] = [];

  // 先添加组成10倍数的三张牌
  tenMultipleIndices.forEach(index => {
    reorderedCards.push(cards[index]);
  });

  // 添加剩余的牌
  cards.forEach((card, index) => {
    if (!tenMultipleIndices.includes(index)) {
      reorderedCards.push(card);
    }
  });
  return reorderedCards;
}

/**
 * 翻牌动画 - 创建扑克牌的翻转效果
 * @param winningArea 获胜区域的ID，用于高亮显示
 * @param revealDelay 开始翻牌前的延迟（毫秒）
 * @param updateHandType 回调函数，用于更新每个区域的牌型结果
 * @param bankerCardsRef 庄家卡牌的ref引用（用于更新）
 * @param sectorCardsRef 天地玄黄卡牌的ref引用（用于更新）
 * @param onBankerFifthCardFlipped 庄家第五张牌翻完的回调函数
 * @returns Promise<Record<string, string>> 各区域的牌型结果
 */
export const flipCardsAnimation = async (
  winningArea?: string,
  revealDelay: number = 500,
  updateHandType?: (areaId: string, handType: string) => void,
  bankerCardsRef?: any,
  sectorCardsRef?: any,
  onBankerFifthCardFlipped?: () => void
): Promise<Record<string, string>> => {

  try {
    // 重置动画停止标志，允许新的动画执行
    animationStopped = false;

    // 加载GSAP动画库
    const gsap = await loadGSAP();
    if (!gsap) throw new Error('GSAP未加载');

    // 注：卡牌排序已在 dealCardsWithData 之前完成，这里直接执行翻牌动画

    // 创建主时间轴
    const timeline = gsap.timeline({
      defaults: {
        duration: 0.5,
        ease: 'power2.out',
        overwrite: 'auto'
      }
    });

    // 获取所有区域ID (包括庄家) - 天地玄黄+庄家
    const areaIds: AreaId[] = ['tian', 'di', 'xuan', 'huang', 'banker'] as AreaId[];
    // 第五张牌的翻转顺序：天地玄黄庄
    const fifthCardFlipOrder: AreaId[] = ['tian', 'di', 'xuan', 'huang', 'banker'] as AreaId[];

    // 翻牌配置 - 顺滑自然的翻牌节奏
    const flipConfig = {
      // 前四张牌翻转延迟 - 流畅节奏
      initialCardsDelay: 0.2,
      // 第五张牌区域间的翻牌延迟 - 增加间隔便于观看（天→地→玄→黄→庄的顺序）
      fifthCardDelay: 1.25,  // 1 → 1.5秒，等待语音播报完成
      // 翻牌动画持续时间 - 顺滑流畅
      flipDuration: 0.3,
      // 结果显示延迟
      resultDelay: 0.3,
      // 顺滑翻牌效果配置
      smoothFlipEffect: {
        // 翻牌前的自然抬起
        liftHeight: 12,
        liftDuration: 0.2,
        // 翻牌时的自然旋转
        flipRotation: 8,
        // 落下时的自然弹跳
        bounceScale: 1.08,
        bounceDuration: 0.2,
        // 顺滑缓动函数
        liftEase: 'power2.out',
        flipEase: 'power2.inOut',
        bounceEase: 'back.out(1.2)',
        settleEase: 'power2.out'
      }
    };

    // 等待指定的开始延迟
    if (revealDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, revealDelay));
    }

    // 翻牌前的整体预告效果
    // const gameTable = document.querySelector('.hall-cover');
    // if (gameTable && gsap) {
    //   gsap.to(gameTable, {
    //     scale: 1.005,
    //     duration: 0.3,
    //     ease: "power2.inOut",
    //     yoyo: true,
    //     repeat: 1
    //   });
    // }

    // 创建一个新对象来保存所有区域的牌型结果
    const resultHandTypes: Record<string, string> = {};

    // 为每个区域计算牌型结果
    for (let areaIndex = 0; areaIndex < areaIds.length; areaIndex++) {
      const areaId = areaIds[areaIndex];
      const areaCardsArray = getAreaCards(areaIndex >= 4 ? 4 : areaIndex); // 处理庄家的特殊索引
      const isWinningArea = winningArea === areaId;

      // 计算该区域的牌型
      const handType = calculateHandType(areaCardsArray);
      resultHandTypes[areaId] = handType;

      // 收集当前区域的前四张卡牌元素，用于批量动画处理
      const firstFourCardElements: CardElement[] = [];

      // 为该区域的前四张卡牌创建翻牌效果
      for (let cardIndex = 0; cardIndex < 4; cardIndex++) {
        const card = areaCardsArray[cardIndex];
        // 确定正确的区域索引（庄家特殊处理）
        const cardAreaIndex = areaIndex >= 4 ? 4 : areaIndex;
        const key = `area${cardAreaIndex}-card${cardIndex}`;
        const cardEl = cardRefs.value.get(key);

        if (!cardEl) {
          console.log(`旧卡牌元素: ${key} 已清理`);
          continue;
        }

        // 确保卡牌是可见的，并重置位置
        const domEl = getDOMElement(cardEl);
        if (domEl) {
          // 清除发牌动画可能留下的transform，重置到正确位置
          gsap.set(domEl, {
            opacity: 1,
            clearProps: "x,y,scale,rotationY"
          });
          // 🔧 关键：clearProps 后恢复 CSS 居中 transform
          domEl.style.transform = 'translateX(-50%) translateZ(0)';
          firstFourCardElements.push(cardEl);
        }
      }

      // 如果没有找到卡牌元素，跳过该区域
      if (firstFourCardElements.length === 0) {
        console.log(`旧区域 ${areaId} 已清理`);
        continue;
      }

      // 前四张牌的翻转开始时间 - 所有区域同时开始翻转
      const cardStartTime = 0.3;

      // 前四张牌翻牌动画：真实翻牌效果
      timeline.call(() => {
        if (animationStopped) return; // 动画已停止，跳过回调

        // 提高z-index确保在翻牌时位于顶层
        const domEls = firstFourCardElements.map(el => getDOMElement(el));
        domEls.forEach(domEl => {
          if (domEl && domEl.style) {
            domEl.style.zIndex = '200';
          }
        });

        // 慢速丝滑的翻牌效果：更长的翻转时间，更柔和的缓动
        firstFourCardElements.forEach((el, idx) => {
          const domEl = getDOMElement(el);
          if (domEl) {
            const frontEl = domEl.querySelector('.front') as HTMLElement;
            const backEl = domEl.querySelector('.back') as HTMLElement;
            if (frontEl && backEl) {
              // 慢速丝滑的翻牌动画
              const gsap = window.gsap;
              if (gsap) {
                const cardDelay = 0; // 所有牌同时翻转，无延迟
                const slowFlipDuration = 0.6; // 更长的翻转时间，更丝滑

                // 设置transform原点
                gsap.set([frontEl, backEl], {
                  transformOrigin: 'center center'
                });

                // 创建丝滑的翻牌时间轴
                const cardTimeline = gsap.timeline({ delay: cardDelay });

                // 背面慢速平滑旋转到180度（隐藏）
                cardTimeline.to(backEl, {
                  rotationY: 180,
                  duration: slowFlipDuration,
                  ease: 'power1.inOut', // 更柔和的缓动
                  transformOrigin: 'center center'
                });

                // 正面慢速平滑旋转到0度（显示）
                cardTimeline.to(frontEl, {
                  rotationY: 0,
                  duration: slowFlipDuration,
                  ease: 'power1.inOut', // 更柔和的缓动
                  transformOrigin: 'center center'
                }, `-=${slowFlipDuration * 0.6}`); // 增加重叠比例，让过渡更自然
              } else {
                // 兜底：直接设置
                frontEl.style.transformOrigin = 'center center';
                backEl.style.transformOrigin = 'center center';
                frontEl.style.transform = 'rotateY(0deg)';
                backEl.style.transform = 'rotateY(180deg)';
              }
            }
          }
        });

        // 延迟恢复z-index（根据慢速翻转时长0.6秒调整）
        setTimeout(() => {
          domEls.forEach(domEl => {
            if (domEl && domEl.style) {
              domEl.style.zIndex = '100';
            }
          });
        }, 600);
      }, [],
        cardStartTime
      );
    }

    // 前四张牌全部翻完后的延迟时间（所有区域同时翻转0.6秒 + 停顿0.5秒）
    const phase2StartDelay = 0.6 + 0.5;

    // 按照指定顺序翻转第五张牌
    for (let orderIndex = 0; orderIndex < fifthCardFlipOrder.length; orderIndex++) {
      const areaId = fifthCardFlipOrder[orderIndex];
      // 查找该区域ID在原始areaIds中的索引
      const areaIndex = areaIds.indexOf(areaId);
      if (areaIndex === -1) continue;

      const cardAreaIndex = areaIndex >= 4 ? 4 : areaIndex; // 庄家特殊处理
      const areaCardsArray = getAreaCards(cardAreaIndex);
      const isWinningArea = winningArea === areaId;

      // 第五张牌的索引
      const cardIndex = 4;
      const card = areaCardsArray[cardIndex];
      const key = `area${cardAreaIndex}-card${cardIndex}`;
      const cardEl = cardRefs.value.get(key);

      if (!cardEl) {
        console.log(`第五张牌元素: ${key} 已清理`);
        continue;
      }

      // 确保卡牌可见，并重置位置
      const domEl = getDOMElement(cardEl);
      if (domEl) {
        // 清除发牌动画可能留下的transform，重置到正确位置
        gsap.set(domEl, {
          opacity: 1,
          clearProps: "x,y,scale,rotationY"
        });
        // 🔧 关键：clearProps 后恢复 CSS 居中 transform
        domEl.style.transform = 'translateX(-50%) translateZ(0)';
      }

      // 第五张牌的开始时间 = 第二阶段开始时间 + 顺序索引 * 延迟
      const cardStartTime = phase2StartDelay + orderIndex * flipConfig.fifthCardDelay;

      // 获取PokerCard组件实例
      const pokerCard = cardEl as PokerCardComponent;

      // ...
      // 第5张牌翻牌动画：更戏剧化的真实翻牌效果
      timeline.call(() => {
        if (animationStopped) return; // 动画已停止，跳过回调

        // 提高z-index确保在翻牌时位于顶层
        const domEl = getDOMElement(cardEl);
        if (domEl && domEl.style) {
          domEl.style.zIndex = '300'; // 更高的层级
        }

        // 第5张牌的戏剧化翻牌效果
        if (domEl) {
          const frontEl = domEl.querySelector('.front') as HTMLElement;
          const backEl = domEl.querySelector('.back') as HTMLElement;
          if (frontEl && backEl) {
            // 第5张牌的顺滑戏剧化翻牌动画
            // 注意：不要使用 window.gsap，直接使用外层的 gsap 变量

            // 创建第5张牌的专用时间轴
            const fifthCardTimeline = gsap.timeline();

            // 第一步：简洁的翻转动画，不抬起
            gsap.set([frontEl, backEl], {
              transformOrigin: 'center center'
            });

            // 背面平滑旋转到180度（隐藏）
            fifthCardTimeline.to(backEl, {
              rotationY: 180,
              duration: flipConfig.flipDuration,
              ease: 'power2.inOut',
              transformOrigin: 'center center'
            });

            // 正面平滑旋转到0度（显示）
            fifthCardTimeline.to(frontEl, {
              rotationY: 0,
              duration: flipConfig.flipDuration,
              ease: 'power2.inOut',
              transformOrigin: 'center center'
            }, `-=${flipConfig.flipDuration * 0.5}`); // 与背面翻转重叠

            // 🎯 第5张牌翻完后：找10倍数、排序、更新数据
            const cardsToLift: HTMLElement[] = [];
            const tenMultipleIndices = findTenMultipleCards(areaCardsArray);

            if (tenMultipleIndices.length === 3) {
              // 🔧 静默替换：在翻牌的同时直接更新牌面内容，无任何滑动动画
              const reorderedCards = reorderCards(areaCardsArray, tenMultipleIndices);

              // 更新动画系统数据
              areaCards.value[areaId] = reorderedCards;

              // 更新原始数据引用
              if (areaId === 'banker' && bankerCardsRef) {
                bankerCardsRef.value = reorderedCards;
              } else if (['tian', 'di', 'xuan', 'huang'].includes(areaId) && sectorCardsRef) {
                sectorCardsRef.value[areaId] = reorderedCards;
              }

              // 静默更新所有5张牌的显示内容
              for (let cardIdx = 0; cardIdx < 5; cardIdx++) {
                const cardKey = `area${cardAreaIndex}-card${cardIdx}`;
                const cardElement = cardRefs.value.get(cardKey);
                if (cardElement) {
                  const domElement = getDOMElement(cardElement);
                  if (domElement) {
                    // 收集前3张牌用于向上移动
                    if (cardIdx < 3) {
                      cardsToLift.push(domElement);
                    }

                    // 静默更新牌面内容
                    const card = reorderedCards[cardIdx];
                    if (typeof cardElement.setCardName === 'function') {
                      let suitName = card.suit.toLowerCase();
                      if (!suitName.endsWith('s')) {
                        suitName = suitName + 's';
                      }
                      const cardIdentifier = `${suitName}${card.rank.toLowerCase()}`;
                      cardElement.setCardName(cardIdentifier);
                    }
                  }
                }
              }
            }

            // 前三张牌向上移动（与翻牌同时进行）
            if (cardsToLift.length > 0) {
              fifthCardTimeline.to(cardsToLift, {
                y: -5,
                duration: 0.3,
                ease: 'power2.out',
                onComplete: () => {
                  cardsToLift.forEach(card => {
                    card.dataset.niuLifted = 'true';
                    card.style.transform = 'translateX(-50%) translateY(-5px) translateZ(0)';
                  });
                }
              }, 0); // 时间标记 0 = 与翻牌同时开始
            }
          }
        }

        // 延迟恢复z-index和更新牌型UI
        setTimeout(() => {
          if (domEl && domEl.style) {
            domEl.style.zIndex = '100';
          }

          // 计算牌型和更新UI（数据已在上面计算过）
          const areaCardsArray = getAreaCards(cardAreaIndex);
          const handType = calculateHandType(areaCardsArray);
          resultHandTypes[areaId] = handType;

          if (updateHandType) {
            updateHandType(areaId, handType);
          }

          // 🎯 如果是庄家第五张牌翻完，触发回调（用于显示通吃通赔）
          if (areaId === 'banker' && onBankerFifthCardFlipped) {
            onBankerFifthCardFlipped();
          }
        }, flipConfig.flipDuration * 1000);
      }, [], cardStartTime);
    }

    // 添加翻牌完成后的整体确认效果
    timeline.call(() => {
      if (animationStopped) return; // 动画已停止，跳过回调

      // 翻牌完成后恢复所有牌的 CSS transform
      const allFlippedCards = document.querySelectorAll('.pocker .card') as NodeListOf<HTMLElement>;
      if (allFlippedCards.length > 0) {
        // 确保最终状态正确
        gsap.set(allFlippedCards, {
          scale: 1,
          clearProps: 'x,y'
        });
        // 🔧 根据 data-niu-lifted 属性恢复正确的 CSS transform
        allFlippedCards.forEach(card => {
          if (card.dataset.niuLifted === 'true') {
            // 有牛区域的前三张牌：保持向上偏移
            card.style.transform = 'translateX(-50%) translateY(-5px) translateZ(0)';
          } else {
            // 无牛区域或后两张牌：正常居中
            card.style.transform = 'translateX(-50%) translateZ(0)';
          }
        });
      }
    }, [], phase2StartDelay + fifthCardFlipOrder.length * flipConfig.fifthCardDelay + 0.8);

    // 等待动画完成
    return new Promise<Record<string, string>>((resolve) => {
      timeline.eventCallback('onComplete', () => {
        resolve(resultHandTypes);
      });
    });
  } catch (error) {
    console.error('执行翻牌动画时出错:', error instanceof Error ? error.message : '未知错误');
    return {};
  }
}
/**
 * 卡牌回收动画 - 将卡牌动画收回到初始发牌位置
 * @param collectDelay 开始回收前的延迟（毫秒）
 * @returns Promise<void> 动画完成的Promise
 */
export const collectCards = async (collectDelay: number = 0): Promise<void> => {
  // 🔧 防止重复调用收牌动画
  if (isCollectingCards) {
    console.log('[收牌动画] 已有收牌动画进行中，跳过');
    return;
  }
  isCollectingCards = true;

  try {
    // 加载GSAP动画库
    const gsap = await loadGSAP();
    if (!gsap) throw new Error('GSAP未加载');

    // 等待延迟时间
    if (collectDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, collectDelay));
    }

    // 创建动画时间轴
    const timeline = gsap.timeline({
      defaults: {
        duration: 0.6,
        ease: 'power2.inOut',
        overwrite: 'auto'
      }
    });

    // 发牌起始点 - 庄家区域中心（页面内，避免撤开页面导致滚动）
    const bankerArea = document.querySelector('.indoor-header .hand-type-result') as HTMLElement;
    const dealerPosition = bankerArea ? {
      x: bankerArea.getBoundingClientRect().left + bankerArea.offsetWidth / 2,
      y: bankerArea.getBoundingClientRect().top + bankerArea.offsetHeight / 2
    } : {
      x: window.innerWidth / 2,
      y: 100 // 默认顶部中心，不会撤开页面
    };

    // 收集所有卡牌元素
    const allCardElements: HTMLElement[] = [];

    // 顺时针收牌顺序，与发牌顺序相反
    const collectingOrder = [4, 3, 2, 1, 0]; // 庄家、黄、玄、地、天

    // 收集所有卡牌元素和对应DOM元素
    const cardElements: Array<{
      domEl: HTMLElement;
      targetOffsetX: number;
      targetOffsetY: number;
      pokerCard?: PokerCardComponent;
    }> = [];

    // 收集所有卡牌信息
    for (const areaIndex of collectingOrder) {
      // 获取区域ID
      const areaId = getAreaId(areaIndex);

      // 收集该区域的5张牌信息
      for (let cardIndex = 4; cardIndex >= 0; cardIndex--) {
        const key = `area${areaIndex}-card${cardIndex}`;
        const cardEl = cardRefs.value.get(key);

        if (cardEl) {
          // 获取真实DOM元素
          const domEl = getDOMElement(cardEl);

          if (!domEl) {
            console.warn(`找不到卡牌DOM元素: ${key}`);
            continue;
          }

          // 重置z-index，确保卡牌回收时在正确的层级
          if (domEl.style) {
            domEl.style.zIndex = '100';
          }

          // 正确处理坐标和卡牌位置
          // 注意：卡牌的transform已经被设置为(0,0)，所以直接设置目标偏移量就可以
          // 获取卡牌在DOM中的原始位置
          const rect = domEl.getBoundingClientRect();
          const cardX = rect.left + rect.width / 2;
          const cardY = rect.top + rect.height / 2;

          // 计算目标偏移量（与发牌动画中的初始偏移量相同）
          const targetOffsetX = dealerPosition.x - cardX;
          const targetOffsetY = dealerPosition.y - cardY;

          // 保存卡牌信息
          cardElements.push({
            domEl,
            targetOffsetX,
            targetOffsetY,
            pokerCard: cardEl as PokerCardComponent
          });
        }
      }
    }

    // 先将所有卡牌翻转到背面
    // cardElements.forEach(item => {
    //   try {
    //     if (item.pokerCard && typeof item.pokerCard.toggleFaceDown === 'function' && item.pokerCard.isFaceUp) {
    //       item.pokerCard.toggleFaceDown();
    //     }
    //   } catch (error) {
    //     console.warn('翻转卡牌失败:', error);
    //   }
    // });

    // 在运动前清除现有的变换，确保卡牌当前位置正确
    cardElements.forEach(item => {
      // 先清除现有的transform，还原到真实位置
      gsap.set(item.domEl, { clearProps: 'transform' });
    });

    // 检查是否有卡牌需要收回
    if (cardElements.length === 0) {
      console.log('[收牌动画] 没有找到卡牌元素，跳过收牌动画');
      isCollectingCards = false;  // 修复：提前返回时也要重置标志
      return;
    }

    // 重新计算偏移量（优化：避免重复计算）
    // 注：上面已经计算过位置，这里只在清除transform后需要重新计算
    cardElements.forEach(item => {
      const rect = item.domEl.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      item.targetOffsetX = dealerPosition.x - centerX;
      item.targetOffsetY = dealerPosition.y - centerY;
    });

    console.log(`[收牌动画] 找到 ${cardElements.length} 张卡牌，开始收牌动画`);

    // 所有卡牌同时回到初始位置
    timeline.to(
      cardElements.map(item => item.domEl),
      {
        x: (i) => cardElements[i].targetOffsetX,
        y: (i) => cardElements[i].targetOffsetY,
        scale: 0.9,  // 轻微缩放，与发牌动画初始状态一致
        opacity: 0,   // 淡出效果
        duration: 0.8,
        ease: 'power1.inOut',
        onStart() {
          // 批量设置z-index
          cardElements.forEach(item => {
            if (item.domEl.style) {
              item.domEl.style.zIndex = '100';
            }
          });
        }
      }
    );

    // 动画完成回调
    return new Promise<void>((resolve) => {
      timeline.eventCallback('onComplete', () => {
        // 优化：不销毁卡牌，只保持隐藏状态（opacity: 0）
        // 发牌时会重置卡牌状态并显示，避免时序问题
        // cleanupCards(); // 移除销毁操作

        // 🔧 修复：在动画完成后重置标志（不能放在 finally 中，因为 finally 会在 return Promise 后立即执行）
        isCollectingCards = false;
        resolve();
      });
    });
  } catch (error) {
    console.error('执行卡牌回收动画时出错:', error instanceof Error ? error.message : '未知错误');
    // 🔧 错误时也要重置标志
    isCollectingCards = false;
    throw new Error('卡牌回收动画执行失败');
  }
};

/**
 * 停止所有卡牌动画
 * 用于退出房间或切换场景时清理动画状态
 */
export const stopAllCardAnimations = (): void => {
  try {

    // 设置停止标志，阻止所有回调函数执行
    animationStopped = true;

    // 获取GSAP实例
    if (typeof window !== 'undefined' && window.gsap) {
      const gsap = window.gsap;

      // 只停止卡牌相关的动画，避免影响其他动画
      const cardSelectors = [
        '.pocker .card',           // 所有卡牌
        '.card .front',            // 卡牌正面
        '.card .back',             // 卡牌背面
        '.poker-card',             // PokerCard组件
        '[class*="pocker-img"]'    // 卡牌位置类
      ];

      cardSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          gsap.killTweensOf(elements);
        }
      });

    }
  } catch (error) {
    console.error('[停止卡牌动画] 停止动画时出错:', error);
  }
};

/**
 * 页面可见性状态
 */
export const isPageVisible = ref(true);

/**
 * 处理页面可见性变化时的筹码同步
 * 比较新旧下注数据，只在数据变化时才清除并重新添加筹码
 */
const handleChipsOnVisibilityChange = (
  currentBets: any[],
  context: {
    lastCurrentRoundBets: any;
    allPlayersTotalValues: any;
    areaTotalValues: any;
    processCurrentRoundBets: any;
    lastProcessedRoundNum: any;
  },
  currentRoundNum?: number
) => {
  /**
   * 比较两个currentRoundBets数组是否相同
   * 通过比较数组长度和每个下注的关键信息来判断
   */
  const areCurrentRoundBetsEqual = (bets1: any[], bets2: any[]): boolean => {
    if (!bets1 || !bets2) return false;
    if (bets1.length !== bets2.length) return false;

    // 创建一个用于比较的字符串表示
    const betToString = (bet: any) => {
      return `${bet.user?._id}_${bet.sector}_${bet.amount}_${bet.currency}`;
    };

    // 将两个数组转换为排序后的字符串集合
    const set1 = new Set(bets1.map(betToString).sort());
    const set2 = new Set(bets2.map(betToString).sort());

    // 比较两个集合是否相同
    if (set1.size !== set2.size) return false;
    for (const item of set1) {
      if (!set2.has(item)) return false;
    }

    return true;
  };

  const lastBets = context.lastCurrentRoundBets.value || [];

  /**
   * 🛡️ 双保险机制：防止筹码累积
   * 
   * 主保险：ON_NIUNIU_STATE → PREPARING_NEXT_ROUND → clearDOMChips()
   *   - 页面可见时正常工作
   * 
   * 副保险：轮次检查（下方代码）
   *   - 页面不可见时，PREPARING_NEXT_ROUND 可能不触发（浏览器节能机制）
   *   - 页面恢复可见时，通过轮次号检测是否跨轮次
   *   - 如果跨轮次，说明错过了 PREPARING_NEXT_ROUND，需要补充清理
   */

  // 🎯 优先检查：轮次是否变化（防止跨轮次时筹码累积）
  if (currentRoundNum !== undefined && context.lastProcessedRoundNum.value > 0 &&
    currentRoundNum !== context.lastProcessedRoundNum.value) {
    console.log(`[页面可见性] 轮次变化（${context.lastProcessedRoundNum.value} → ${currentRoundNum}），清空旧筹码并显示新筹码`);

    // 清空所有旧筹码
    try {
      const chipsToRemove = [
        ...document.querySelectorAll('.permanent-chip-container'),
        ...document.querySelectorAll('.other-player-permanent-chip'),
        ...document.querySelectorAll('.other-player-chip-container'),
        ...document.querySelectorAll('.flying-chip')
      ];
      chipsToRemove.forEach(chip => chip.remove());
      if (chipsToRemove.length > 0) {
        console.log(`[页面可见性-跨轮次] 已清除 ${chipsToRemove.length} 个旧筹码`);
      }
    } catch (error) {
      console.error('[页面可见性] 清除筹码失败:', error);
    }

    // 重置统计数据
    context.allPlayersTotalValues.value = { tian: 0, di: 0, xuan: 0, huang: 0 };
    context.areaTotalValues.value = { tian: 0, di: 0, xuan: 0, huang: 0 };

    // 显示新一轮的筹码
    if (currentBets.length > 0) {
      setTimeout(() => {
        context.processCurrentRoundBets(currentBets, true);
      }, 300);
    }

    // 更新轮次和下注数据
    context.lastProcessedRoundNum.value = currentRoundNum;
    context.lastCurrentRoundBets.value = JSON.parse(JSON.stringify(currentBets));
    return;
  }

  // 🔹 特殊情况：如果 lastBets 为空但 currentBets 有数据
  if (lastBets.length === 0 && currentBets.length > 0) {
    // 检查页面上是否有筹码DOM
    const existingChips = document.querySelectorAll('.permanent-chip-container, .other-player-permanent-chip');

    if (existingChips.length > 0) {
      // 页面上有筹码，保留现有筹码，只保存数据
      console.log(`[页面可见性] 首次记录下注数据（${currentBets.length}笔），保留现有 ${existingChips.length} 个筹码`);
      context.lastCurrentRoundBets.value = JSON.parse(JSON.stringify(currentBets));
      if (currentRoundNum !== undefined) {
        context.lastProcessedRoundNum.value = currentRoundNum;
      }
      return;
    } else {
      // 页面上没有筹码（离开很久后回来），需要显示筹码
      console.log(`[页面可见性] 首次记录且页面无筹码，显示 ${currentBets.length} 笔下注的筹码`);
      setTimeout(() => {
        context.processCurrentRoundBets(currentBets, true);
      }, 300);
      context.lastCurrentRoundBets.value = JSON.parse(JSON.stringify(currentBets));
      if (currentRoundNum !== undefined) {
        context.lastProcessedRoundNum.value = currentRoundNum;
      }
      return;
    }
  }

  // 比较新旧下注数据是否相同
  const betsAreEqual = areCurrentRoundBetsEqual(currentBets, lastBets);

  if (betsAreEqual) {
    // 数据相同，不需要重新处理筹码
    console.log(`[页面可见性] 下注数据未变化（${currentBets.length}笔），保留现有筹码`);
    return;
  }

  // 🎯 数据不同，找出新增的下注（增量更新）
  console.log(`[页面可见性] 下注数据已变化（旧:${lastBets.length}笔 → 新:${currentBets.length}笔），增量更新筹码`);

  // betToString 已在 areCurrentRoundBetsEqual 中定义，这里直接使用
  const createBetKey = (bet: any) => {
    return `${bet.user?._id}_${bet.sector}_${bet.amount}_${bet.currency}`;
  };

  // 将已有的下注转换为 Set，便于快速查找
  const existingBetsSet = new Set(lastBets.map(createBetKey));

  // 找出新增的下注（在 currentBets 中但不在 lastBets 中）
  const newBets = currentBets.filter(bet => !existingBetsSet.has(createBetKey(bet)));

  if (newBets.length > 0) {
    console.log(`[页面可见性] 发现 ${newBets.length} 笔新增下注，只添加新增筹码`);

    // 只处理新增的下注，不清空现有筹码
    setTimeout(() => {
      context.processCurrentRoundBets(newBets, true);
    }, 300);
  } else {
    console.log(`[页面可见性] 没有新增下注，可能是下注减少（用户取消下注？），保留现有筹码`);
  }

  // 更新保存的下注数据和轮次号
  context.lastCurrentRoundBets.value = JSON.parse(JSON.stringify(currentBets));
  if (currentRoundNum !== undefined) {
    context.lastProcessedRoundNum.value = currentRoundNum;
  }
};

// 保存最后处理的游戏轮次号（用于检测跨轮次，避免筹码累积）
const lastProcessedRoundNum = ref<number>(0)

// 保存页面可见性处理过的开奖轮次号（防止同一轮次重复处理开奖）
let lastVisibilityProcessedRoundNum = 0;

// 记录当前轮次是否已通过 WebSocket 实时开奖（防止页面可见性恢复时重复处理）
let hasRealtimeResultProcessed = false;
let realtimeProcessedRoundNum = 0;

/**
 * 标记当前轮次已通过 WebSocket 实时开奖
 * 由 ON_NIUNIU_RESULT 监听器调用
 */
export const markRealtimeResultProcessed = (roundNum: number) => {
  hasRealtimeResultProcessed = true;
  realtimeProcessedRoundNum = roundNum;
};

/**
 * 重置实时开奖标志（新一轮开始时调用）
 */
export const resetRealtimeResultFlag = () => {
  hasRealtimeResultProcessed = false;
  realtimeProcessedRoundNum = 0;
};

let pageHiddenTime = 0;
// 长时间离开的阈值（毫秒）- 默认5分钟
const LONG_ABSENCE_THRESHOLD = 5 * 60 * 1000;

export const createVisibilityHandler = (context: {
  oMusic: any;
  route: any;
  EMIT_NIUNIU_JOIN: any;
  gameStatus: any;
  currentStateEndTime: any;
  countdownValue: any;
  gameTimers: any;
  winningSectors: any;
  showTakeLoseAllResult: any;
  indoorHeaderRef: any;
  hallCoverRef: any;
  bettingEnabled: any;
  showStatus: any;
  hasDealtCardsThisRound: any;
  executeCardDeal: any;
  processRoundResult: any;
  handleRealtimeGameResult: any;
  processCurrentRoundBets: any;
  resetGame: any;
  clearHistoryBetTimers: any;
  allPlayersTotalValues: any;
  areaTotalValues: any;
  isProcessingInitialResult: any;
  fnBackToPortal: any;
  getTables: any;
  tables: any;
  currentCurrency: any;
  userStore: any;
  fnCurrentCurrency: any;
  isJustEnteredRoom: any;
  handleCountDown: any;
  lastCurrentRoundBets: any;
  lastProcessedRoundNum: any;
  showNotBettingTip: any;
  cleanupRoomResources?: any;
  bankerCards: any;
  sectorCards: any;
  $nextTick?: any;
  cleanupSettlementChips?: () => void;
}) => {
  return () => {
    isPageVisible.value = document.visibilityState === 'visible';

    if (document.visibilityState === 'visible') {
      console.log('页面可见------------')

      // ✅ 先清理之前的动画状态，确保干净
      stopAllCardAnimations();
      console.log('[页面可见性] 已清理旧动画状态');

      // 检测长时间离开
      const absenceTime = pageHiddenTime > 0 ? Date.now() - pageHiddenTime : 0;
      const isLongAbsence = absenceTime > LONG_ABSENCE_THRESHOLD;

      if (isLongAbsence) {
        console.log(`[页面可见性] 检测到长时间离开（${(absenceTime / 1000 / 60).toFixed(1)}分钟），执行静默刷新`);
        pageHiddenTime = 0;

        if (context.cleanupRoomResources && typeof context.cleanupRoomResources === 'function') {
          context.cleanupRoomResources(true).then(() => {
            console.log('[页面可见性] 静默刷新完成，已重新加入房间');
          }).catch((err: any) => {
            console.error('[页面可见性] 静默刷新失败:', err);
          });
        } else {
          console.warn('[页面可见性] 未提供 cleanupRoomResources 函数，执行硬刷新');
          window.location.reload();
        }
        return;
      }

      pageHiddenTime = 0;

      if (context.clearHistoryBetTimers) {
        context.clearHistoryBetTimers();
      }

      if (context.gameTimers && context.gameTimers.resultTimers) {
        const timerCount = context.gameTimers.resultTimers.length;
        if (timerCount > 0) {
          context.gameTimers.resultTimers.forEach(timerId => {
            if (timerId) clearTimeout(timerId);
          });
          context.gameTimers.resultTimers = [];
        }
      }

      if (context.oMusic && import.meta.client) {
        try {
          Object.values(context.oMusic.oAudio).forEach((audio: any) => {
            if (audio.isBGM) {
              audio.mute(false);
              if (!audio.playing()) {
                audio.toPlay();
              }
            }
          });
        } catch (error) {
          console.warn('[页面可见性] 恢复音乐失败:', error);
        }
      }

      const params = {
        hallName: context.route?.query?.name as string,
        rateType: "x" + context.route?.params?.id
      };

      context.EMIT_NIUNIU_JOIN(params, async (res: any) => {
        console.log(res, '页面可见----')

        // 重要保护：如果接口延迟返回时页面已不可见，直接跳过处理
        if (!isPageVisible.value) {
          console.log('[页面可见性] 接口返回时页面已不可见，跳过处理');
          return;
        }

        if (res?.['code'] == '3308') {
          context.fnBackToPortal();
          return;
        }

        if (!res || res?.error) {
          return;
        }

        const configTables = context.getTables();
        if (configTables.length > 0) {
          context.tables.value = configTables;
        }

        context.gameStatus.value = res?.state?.name;

        const currency = context.currentCurrency.value || context.userStore.$state.currentCurrency;
        const hallLimit = res[currency]?.hallLimit;
        context.fnCurrentCurrency(hallLimit);

        context.isJustEnteredRoom.value = true;
        context.handleCountDown(res.state);

        const hasVisibleCards = document.querySelector('.pocker .card[style*="opacity: 1"]');

        switch (context.gameStatus.value) {
          case 'PREPARING_NEXT_ROUND':
            context.bettingEnabled.value = false;
            context.showStatus.value = true;
            context.showNotBettingTip.value = true
            resetRealtimeResultFlag();
            break;

          case 'BETTING':
            context.bettingEnabled.value = true;
            context.showStatus.value = context.countdownValue.value > 14;

            if (!hasVisibleCards) {
              if (context.countdownValue.value <= 3) {
                // 不发牌
              } else if (context.countdownValue.value > 14) {
                context.hasDealtCardsThisRound.value = true;
                context.executeCardDeal(0.4);
              } else {
                context.hasDealtCardsThisRound.value = true;
                context.executeCardDeal(0.1);
              }
            } else {
              context.hasDealtCardsThisRound.value = true;
            }
            break;

          case 'STOP_BETTING':
            // 停止下注阶段：禁止下注，显示提示
            context.bettingEnabled.value = false;
            context.showStatus.value = true;
            context.hasDealtCardsThisRound.value = true;

            // 如果页面无卡牌，极速发牌显示背面
            if (!hasVisibleCards) {
              context.executeCardDeal(0);
            }
            break;

          case 'SHOW_RESULT':
            context.bettingEnabled.value = false;
            context.showStatus.value = false;
            context.hasDealtCardsThisRound.value = true;
            console.log(context.countdownValue.value, '----------------------')

            // 🎯 保护机制1：防止重复处理
            if (context.isProcessingInitialResult.value) {
              context.showNotBettingTip.value = true;
              break;
            }

            // 🔍 获取当前轮次号
            const currentRoundNum = res.roundNum || res.roundResult?.roundNum || res.state?.roundNum || 0;

            // 🎯 保护机制2：检查是否已通过 WebSocket 实时开奖处理过
            if (hasRealtimeResultProcessed && currentRoundNum === realtimeProcessedRoundNum) {
              context.showNotBettingTip.value = true;
              break;
            }

            // 🎯 保护机制3：检查是否已通过页面可见性处理过
            if (currentRoundNum > 0 && currentRoundNum === lastVisibilityProcessedRoundNum) {
              context.showNotBettingTip.value = true;
              break;
            }

            // 🎬 根据剩余时间决定处理方式
            if (context.countdownValue.value > 6 && res.roundResult) {
              // 重要保护：如果接口延迟返回时已经在处理开奖，直接跳过
              if (context.isProcessingInitialResult.value) {
                context.showNotBettingTip.value = true;
                break;
              }
              // 时间充足：执行完整动画
              context.isProcessingInitialResult.value = true;
              lastVisibilityProcessedRoundNum = currentRoundNum;

              // 🔧 标准化数据格式：确保 sectorResultsByRoom 字段存在
              const normalizedResult = { ...res.roundResult };
              const currentRateType = "x" + context.route?.params?.id;

              if (!normalizedResult.sectorResultsByRoom?.[currentRateType] && normalizedResult.TianNiuType !== undefined) {
                const multipliers = normalizedResult.MultiplierSectors ? JSON.parse(normalizedResult.MultiplierSectors) : {};
                normalizedResult.sectorResultsByRoom = {
                  [currentRateType]: {
                    tian: { niuType: normalizedResult.TianNiuType, multi: multipliers.tian || 1 },
                    di: { niuType: normalizedResult.DiNiuType, multi: multipliers.di || 1 },
                    xuan: { niuType: normalizedResult.XuanNiuType, multi: multipliers.xuan || 1 },
                    huang: { niuType: normalizedResult.HuangNiuType, multi: multipliers.huang || 1 }
                  }
                };
              }

              context.handleRealtimeGameResult(normalizedResult).finally(() => {
                context.isProcessingInitialResult.value = false;
              });
            } else if (context.countdownValue.value >= 2 && context.countdownValue.value <= 6 && res.roundResult) {
              // 时间不足：快速显示结果
              context.isProcessingInitialResult.value = true;
              lastVisibilityProcessedRoundNum = currentRoundNum;
              // 🔧 标准化数据格式：确保 sectorResultsByRoom 字段存在
              const normalizedResultFast = { ...res.roundResult };
              const currentRateTypeFast = "x" + context.route?.params?.id;

              if (!normalizedResultFast.sectorResultsByRoom?.[currentRateTypeFast] && normalizedResultFast.TianNiuType !== undefined) {
                const multipliers = normalizedResultFast.MultiplierSectors ? JSON.parse(normalizedResultFast.MultiplierSectors) : {};
                normalizedResultFast.sectorResultsByRoom = {
                  [currentRateTypeFast]: {
                    tian: { niuType: normalizedResultFast.TianNiuType, multi: multipliers.tian || 1 },
                    di: { niuType: normalizedResultFast.DiNiuType, multi: multipliers.di || 1 },
                    xuan: { niuType: normalizedResultFast.XuanNiuType, multi: multipliers.xuan || 1 },
                    huang: { niuType: normalizedResultFast.HuangNiuType, multi: multipliers.huang || 1 }
                  }
                };
              }

              setTimeout(() => {
                if (!isPageVisible.value) {
                  context.isProcessingInitialResult.value = false;
                  return;
                }

                context.processRoundResult(normalizedResultFast, true).finally(() => {
                  context.isProcessingInitialResult.value = false;
                  // 🎯 等待开奖动画完成后再处理筹码（避免视觉混乱）
                  const currentBets = res.currentRoundBets && Array.isArray(res.currentRoundBets) ? res.currentRoundBets : [];
                  handleChipsOnVisibilityChange(currentBets, {
                    lastCurrentRoundBets: context.lastCurrentRoundBets,
                    allPlayersTotalValues: context.allPlayersTotalValues,
                    areaTotalValues: context.areaTotalValues,
                    processCurrentRoundBets: context.processCurrentRoundBets,
                    lastProcessedRoundNum: context.lastProcessedRoundNum
                  }, res.roundNum);
                });
              }, 300);

            } else if (!hasVisibleCards) {
              // 无历史数据：显示背面牌
              context.executeCardDeal(0);
            }
            context.showNotBettingTip.value = true;
            break;
        }
        // 🎯 页面恢复可见时的筹码处理（非 SHOW_RESULT 状态立即处理）
        if (context.gameStatus.value !== 'SHOW_RESULT' && context.gameStatus.value !== 'PREPARING_NEXT_ROUND') {
          const currentBets = res.currentRoundBets && Array.isArray(res.currentRoundBets) ? res.currentRoundBets : [];
          handleChipsOnVisibilityChange(currentBets, {
            lastCurrentRoundBets: context.lastCurrentRoundBets,
            allPlayersTotalValues: context.allPlayersTotalValues,
            areaTotalValues: context.areaTotalValues,
            processCurrentRoundBets: context.processCurrentRoundBets,
            lastProcessedRoundNum: context.lastProcessedRoundNum
          }, res.roundNum);
        }
      });

    } else {
      // 记录页面离开的时间
      pageHiddenTime = Date.now();
      console.log('[页面可见性] 页面隐藏，记录离开时间');

      // 静音音频
      if (context.oMusic && import.meta.client) {
        try {
          Object.values(context.oMusic.oAudio).forEach((audio: any) => {
            if (audio.isBGM) {
              audio.mute(true);
            }
          });
        } catch (error) {
          console.warn('[页面可见性] 静音失败:', error);
        }
      }

      // 页面不可见时：停止卡牌动画
      stopAllCardAnimations();

      // 🎯 停止筹码结算动画
      if (context.cleanupSettlementChips && typeof context.cleanupSettlementChips === 'function') {
        context.cleanupSettlementChips();
        console.log('[页面可见性] 已停止筹码结算动画');
      }

      // 清除所有定时器（恢复时会重新同步）
      if (context.gameTimers.globalTimer) {
        clearInterval(context.gameTimers.globalTimer);
        context.gameTimers.globalTimer = null;
      }

      // 清理历史下注定时器
      if (context.clearHistoryBetTimers) {
        context.clearHistoryBetTimers();
      }

    }
  };
};

// 导出统一的卡牌动画模块
export default {
  // 核心函数
  loadGSAP,
  dealCardsWithData,
  animateCards,
  flipCardsAnimation,
  collectCards,
  stopAllCardAnimations,

  // 工具函数
  setAreaCardRef,
  getAreaCards,
  getAreaId,
  findTenMultipleCards,
  reorderCards,
  clearCardRefs,
  cleanupCards,
  ensureAllCardsShowBack,

  // 状态变量
  cardRefs,
  fullDeck
};